// Sesión del cliente en esta tienda (decisiones D44, D48 y D51): tokens en localStorage por tienda. El access dura 30 minutos y el
// refresh rota en cada uso (el nuevo reemplaza al anterior).
import { create } from 'zustand';
import { alCambiarEnOtraPestana, borrar, guardar, leer } from './almacen';
import { claveLocal } from './tienda-actual';
import type { CuentaGoogle, CuentaTienda, Tokens } from './tipos';

/** El preventista que entró con su usuario del personal (decisión D60): la tienda en "modo vendedor". */
export interface VendedorSesion {
  id: string;
  nombre: string;
  email: string | null;
}

/**
 * De una cuenta (correo, Google, Apple o Facebook; decisión D51), con el celular prendido de un teléfono, o de un
 * preventista (`vendedor`, decisión D60): sus tokens son los del personal y se refrescan por `/auth/refresh`.
 */
export interface Sesion extends Tokens {
  telefono: string | null;
  identidad_id?: string | null;
  cuenta?: CuentaTienda | null;
  google?: CuentaGoogle | null;
  vendedor?: VendedorSesion | null;
}

export type MotivoSalida = 'vencida' | 'revocada' | 'salio';

interface EstadoSesion {
  sesion: Sesion | null;
  /** Por qué se cerró la última sesión (para avisarlo en la pantalla de entrada). */
  motivo: MotivoSalida | null;
}

const CLAVE = claveLocal('sesion');

export const useSesion = create<EstadoSesion>(() => ({ sesion: leerSesionGuardada(), motivo: null }));

/** La guardada en el celular: puede ser más nueva que la de memoria si otra pestaña la rotó. */
export function leerSesionGuardada(): Sesion | null {
  const s = leer<Sesion>(CLAVE);
  return s?.access_token && s.refresh_token ? s : null;
}

/** El preventista de la sesión, si la tienda está en modo vendedor. */
export const vendedorDeSesion = (): VendedorSesion | null => useSesion.getState().sesion?.vendedor ?? null;
export const useVendedor = (): VendedorSesion | null => useSesion((s) => s.sesion?.vendedor ?? null);

export function fijarSesion(sesion: Sesion | null, motivo: MotivoSalida | null = null): void {
  if (sesion) guardar(CLAVE, sesion);
  else borrar(CLAVE);
  useSesion.setState({ sesion, motivo: sesion ? null : motivo });
}

/** Guarda los tokens nuevos de un refresh conservando quién es (teléfono o Google). */
export function rotarTokens(t: Tokens): void {
  const actual = useSesion.getState().sesion ?? leerSesionGuardada();
  if (!actual) return;
  fijarSesion({ ...actual, ...t });
}

if (typeof window !== 'undefined') {
  // Dos pestañas abiertas: la que rota los tokens se los pasa a la otra; la que sale, saca a la otra.
  alCambiarEnOtraPestana(CLAVE, (v) => {
    const s = v as Sesion | null;
    useSesion.setState({ sesion: s?.access_token ? s : null, motivo: s ? null : 'salio' });
  });
}
